import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function QRExport() {
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState([]);
  const [format, setFormat] = useState("png");
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    api.get("/cards", { params: { limit: 1000 } }).then((r) => setCards(r.data.cards));
  }, []);

  const filtered = statusFilter === "ALL" ? cards : cards.filter((c) => c.status === statusFilter);
  const toggle = (code) => setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
  const allSel = filtered.length > 0 && filtered.every((c) => selected.includes(c.card_code));
  const toggleAll = () => setSelected(allSel ? [] : filtered.map((c) => c.card_code));

  const doExport = async () => {
    if (selected.length === 0) return toast.error("Select at least one card");
    try {
      const r = await api.post("/cards/export", { card_codes: selected, format }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "revnex_qr_export.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${selected.length} QR codes`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="qr-export-page">
      <h1 className="text-2xl font-bold mb-1">QR Export</h1>
      <p className="text-gray-500 mb-6 text-sm">
        Generate a ZIP of QR files (+ manifest.csv) to send to your card supplier. Cards can be printed while UNASSIGNED.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Select Cards</span>
            <div className="flex gap-2 items-center">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 h-9" data-testid="export-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["ALL", "UNASSIGNED", "RESERVED", "ACTIVE", "SUSPENDED", "RETIRED"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="w-28 h-9" data-testid="export-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="png">PNG</SelectItem>
                  <SelectItem value="svg">SVG</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={doExport} disabled={selected.length === 0} data-testid="export-zip-button">
                Export ZIP ({selected.length})
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-2 text-sm mb-3">
            <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="export-select-all" /> Select all ({filtered.length})
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[60vh] overflow-auto">
            {filtered.map((c) => (
              <label
                key={c.card_code}
                className={`border rounded-md p-3 flex items-center gap-2 cursor-pointer text-sm ${selected.includes(c.card_code) ? "border-gray-900 bg-gray-50" : ""}`}
                data-testid={`export-card-${c.card_code}`}
              >
                <Checkbox checked={selected.includes(c.card_code)} onCheckedChange={() => toggle(c.card_code)} />
                <span className="font-mono">{c.card_code}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
